// @ts-check
import React, { useEffect, useState } from 'react';

import LWForm from 'components/LWForm';
import HookedSubmit from 'components/Form/HookedSubmit';
import HookedSwitch from 'components/Form/HookedSwitch';
import HookedTextField from 'components/Form/HookedTextField';
import { HookedServerSpecsCard } from 'components/SeverSpecsCard';
import useServerWhmFirewallUnblockip from 'modules/queries/server/whm/firewall/useUnblockip';
import useWhatismyip from 'modules/queries/utilities/info/useWhatismyip';
import Box from '@liquidweb-dev/undercurrent/Box';
import Button from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CodeSnippet from '@liquidweb-dev/undercurrent/CodeSnippet';
import Drawer from '@liquidweb-dev/undercurrent/Drawer';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { switchClasses } from '@liquidweb-dev/undercurrent/Switch';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import isIP from 'validator/es/lib/isIP';

/**
 * @param {Omit<import('@liquidweb-dev/undercurrent/Drawer').DrawerProps, 'onClose'> & {
 *   onClose: () => void,
 *   uniqId: string,
 * }} drawerProps
 * */
const DrawerUnblockIp = ({ onClose, uniqId, ...drawerProps }) => {
	const [formValues, setFormValues] = useState({ ip: '', whitelist: false });
	const [formErrors, setFormErrors] = useState({});

	const { data: myIpData, isLoading: isLoadingMyIp } = useWhatismyip({
		enabled: Boolean(drawerProps.open),
	});

	const myIp = myIpData?.ip;

	useEffect(() => {
		setFormValues((prev) => ({ ...prev, ip: myIp || '' }));
	}, [myIp]);

	const { mutate: unblockIp, isLoading: isUnblockingIp } =
		useServerWhmFirewallUnblockip();

	/**
	 * @param {React.MouseEvent<HTMLButtonElement, MouseEvent>} event
	 * @param {string | null | undefined} text
	 * */
	const handleCopy = (event, text) => {
		if (!text) {
			// prevent tooltip from showing
			event.preventDefault();

			return;
		}

		navigator.clipboard.writeText(text);
	};

	const handleClose = () => {
		setFormValues({
			ip: myIp || '',
			whitelist: false,
		});
		setFormErrors({});
		onClose();
	};

	const handleSubmit = () => {
		unblockIp({
			ip: formValues.ip,
			uniq_id: uniqId,
			whitelist: formValues.whitelist,
		});
		handleClose();
	};

	return (
		<Drawer anchor="right" onClose={handleClose} {...drawerProps}>
			<DrawerHeader onClose={handleClose}>Unblock IPs</DrawerHeader>

			<LWForm
				errors={formErrors}
				onErrorsChange={setFormErrors}
				onSubmit={handleSubmit}
				onValuesChange={setFormValues}
				values={formValues}
				validators={{
					ip: (value) => {
						if (typeof value !== 'string' || !isIP(value, 4)) {
							return 'Valid IPv4 address required';
						}
						return '';
					},
				}}
				style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					height: '100%',
				}}
			>
				<DrawerContent>
					<Stack sx={{ gap: 200 }}>
						<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
							What will this do?
						</Typography>
						<Typography variant="body-md">
							Your server&apos;s firewall protects against attacks by filtering
							traffic according to rules. It can block IPs violating these
							rules, but sometimes legitimate IPs may inadvertently get blocked,
							making your site appear down. After checking if the site is down
							for everyone, unblocking your IP is the next step to getting
							access back.
						</Typography>
					</Stack>

					<Stack>
						<Card
							sx={{
								borderBottom: 'none',
								borderBottomLeftRadius: 0,
								borderBottomRightRadius: 0,
							}}
						>
							<CardContent
								sx={{ display: 'flex', flexDirection: 'column', gap: 300 }}
							>
								<Box sx={{ display: 'flex', gap: 50, alignItems: 'center' }}>
									{isLoadingMyIp ? (
										<Skeleton variant="rounded" width="100%" height="24px" />
									) : (
										<>
											<Typography variant="body-md">
												Your public IP address is
											</Typography>

											<Box
												sx={{ display: 'flex', gap: 100, alignItems: 'center' }}
											>
												<CodeSnippet
													onCopyIconClick={(event) => handleCopy(event, myIp)}
												>
													{myIp}
												</CodeSnippet>
											</Box>
										</>
									)}
								</Box>

								<HookedTextField
									name="ip"
									label="IP address to unblock"
									disabled={isLoadingMyIp}
								/>
							</CardContent>
						</Card>

						<Box
							sx={({ vars, borderRadius }) => ({
								display: 'flex',
								alignItems: 'center',
								padding: 200,
								height: '56px',
								backgroundColor: formValues.whitelist
									? vars.palette.uc.success.bg.main
									: undefined,
								borderWidth: '1px',
								borderStyle: 'solid',
								borderColor: formValues.whitelist
									? vars.palette.uc.success.border.weak
									: vars.palette.uc.border.weak,
								borderBottomLeftRadius: borderRadius.rounded,
								borderBottomRightRadius: borderRadius.rounded,
								transition: 'all 0.2s ease-in-out',
							})}
						>
							<HookedSwitch
								name="whitelist"
								label="Whitelist this IP to prevent future blocks"
								color="success"
								sx={{
									[`& .${switchClasses.switchBase}.${switchClasses.checked} + .${switchClasses.track}`]:
										{
											backgroundColor: ({ vars }) =>
												vars.palette.uc.success.bg.strong,
										},
								}}
							/>
						</Box>
					</Stack>

					<HookedServerSpecsCard
						cardHeaderTitle="Affected server"
						uniqId={uniqId}
					/>
				</DrawerContent>

				<DrawerFooter sx={{ gap: 100 }}>
					<HookedSubmit loading={isUnblockingIp} size="large">
						Confirm
					</HookedSubmit>
					<Button variant="ghost" onClick={handleClose} size="large">
						Cancel
					</Button>
				</DrawerFooter>
			</LWForm>
		</Drawer>
	);
};

export default DrawerUnblockIp;
